/// <reference path="./transforms.d.ts" />
import { pushGraphicsState, popGraphicsState, concatTransformationMatrix } from 'pdf-lib';
import { PageGen, PdfPoint } from '../pdf';

/**
 * Save the current graphics state.
 * Port of C# XGraphics.Save()
 */
PageGen.prototype.save = function (): void {
  this.page.pushOperators(pushGraphicsState());
};

/**
 * Restore the previous graphics state.
 * Port of C# XGraphics.Restore()
 */
PageGen.prototype.restore = function (): void {
  this.page.pushOperators(popGraphicsState());
};

/**
 * Apply a rotation transform around a specified point.
 * Port of C# XGraphics.RotateAtTransform()
 *
 * @param angleDegrees Rotation angle in degrees (positive = counterclockwise)
 * @param center Point to rotate around
 */
PageGen.prototype.rotateAtTransform = function (angleDegrees: number, center: PdfPoint): void {
  // Convert to radians
  const angleRadians = (angleDegrees * Math.PI) / 180;
  const cos = Math.cos(angleRadians);
  const sin = Math.sin(angleRadians);

  // Get the center point in PDF coordinates (bottom-left origin)
  const { x, y } = this.getXY(center);

  // Rotation matrix around point (cx, cy):
  // 1. Translate to origin: T(-cx, -cy)
  // 2. Rotate: R(angle)
  // 3. Translate back: T(cx, cy)
  // Combined: [cos, sin, -sin, cos, cx*(1-cos)+cy*sin, cy*(1-cos)-cx*sin]

  const a = cos;
  const b = sin;
  const c = -sin;
  const d = cos;
  const e = x * (1 - cos) + y * sin;
  const f = y * (1 - cos) - x * sin;

  // Apply the transformation matrix
  this.page.pushOperators(concatTransformationMatrix(a, b, c, d, e, f));
};

/**
 * Apply a translation transform.
 *
 * @param dx X offset
 * @param dy Y offset
 */
PageGen.prototype.translateTransform = function (dx: number, dy: number): void {
  // Translation matrix: [1, 0, 0, 1, dx, dy]
  this.page.pushOperators(concatTransformationMatrix(1, 0, 0, 1, dx, -dy));
};

/**
 * Apply a scale transform.
 *
 * @param scaleX X scale factor
 * @param scaleY Y scale factor
 */
PageGen.prototype.scaleTransform = function (scaleX: number, scaleY: number): void {
  // Scale matrix: [scaleX, 0, 0, scaleY, 0, 0]
  this.page.pushOperators(concatTransformationMatrix(scaleX, 0, 0, scaleY, 0, 0));
};
